import React, { useState, useEffect, useCallback } from 'react';
import { Elements, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type {
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementShippingRateChangeEvent,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import { getStripePromise } from '../utils/stripe-config';
import { useCart } from '../context/CartContext';
import { calculateTotal } from '../functions/calculateTotal';
import { useTranslation } from 'react-i18next';
import { getUtmForCheckout } from '../utils/utm';

const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
];

const FREE_SHIPPING_THRESHOLD = 200;
const STANDARD_SHIPPING_CENTS = 1600;
const EXPRESS_SHIPPING_CENTS = 2500;

/**
 * Inner component — must be a child of <Elements> to access stripe/elements.
 */
const CheckoutForm: React.FC<{ paymentIntentId: string }> = ({ paymentIntentId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { items } = useCart();
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);
  const total = calculateTotal(items);
  const qualifiesFreeShipping = total >= FREE_SHIPPING_THRESHOLD;

  const onReady = useCallback((event: StripeExpressCheckoutElementReadyEvent) => {
    if (event.availablePaymentMethods) {
      setReady(true);
    }
  }, []);

  const onShippingAddressChange = useCallback(
    async (event: StripeExpressCheckoutElementShippingAddressChangeEvent) => {
      const { address, resolve, reject } = event;

      if (!EU_COUNTRIES.includes(address.country)) {
        reject();
        return;
      }

      const rates = [
        {
          id: qualifiesFreeShipping ? 'free-standard' : 'standard',
          amount: qualifiesFreeShipping ? 0 : STANDARD_SHIPPING_CENTS,
          displayName: qualifiesFreeShipping ? 'Free Standard Shipping' : 'Standard Shipping',
          deliveryEstimate: { minimum: { unit: 'business_day' as const, value: 5 }, maximum: { unit: 'business_day' as const, value: 7 } },
        },
        {
          id: 'express',
          amount: EXPRESS_SHIPPING_CENTS,
          displayName: 'Express Shipping',
          deliveryEstimate: { minimum: { unit: 'business_day' as const, value: 1 }, maximum: { unit: 'business_day' as const, value: 2 } },
        },
      ];

      // Update PaymentIntent with the first (default) rate
      const defaultRate = rates[0];
      try {
        await fetch('/api/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'update-intent',
            paymentIntentId,
            shippingAmount: defaultRate.amount / 100,
            shippingMethod: defaultRate.displayName,
          }),
        });
      } catch (err) {
        console.error('Failed to update shipping:', err);
      }

      resolve({ shippingRates: rates });
    },
    [paymentIntentId, qualifiesFreeShipping]
  );

  const onShippingRateChange = useCallback(
    async (event: StripeExpressCheckoutElementShippingRateChangeEvent) => {
      const { shippingRate, resolve } = event;

      try {
        await fetch('/api/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'update-intent',
            paymentIntentId,
            shippingAmount: shippingRate.amount / 100,
            shippingMethod: shippingRate.displayName,
          }),
        });
      } catch (err) {
        console.error('Failed to update shipping rate:', err);
      }

      resolve();
    },
    [paymentIntentId]
  );

  const onConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements) {
        event.paymentFailed({ reason: 'fail' });
        return;
      }

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/success`,
          receipt_email: event.billingDetails?.email || undefined,
        },
      });

      if (error) {
        console.error('Express checkout error:', error);
        event.paymentFailed({ reason: 'fail' });
      }
      // On success, Stripe auto-redirects to return_url
    },
    [stripe, elements]
  );

  return (
    <div className={ready ? '' : 'hidden'}>
      <ExpressCheckoutElement
        options={{
          buttonHeight: 48,
          buttonType: {
            applePay: 'buy',
            googlePay: 'buy',
          },
          shippingAddressRequired: true,
          emailRequired: true,
          phoneNumberRequired: false,
          allowedShippingCountries: EU_COUNTRIES,
        }}
        onReady={onReady}
        onConfirm={onConfirm}
        onShippingAddressChange={onShippingAddressChange}
        onShippingRateChange={onShippingRateChange}
      />
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 border-t border-gray-200 dark:border-brand-charcoal" />
        <span className="text-xs text-brand-gray">{t('trust.expressCheckoutDivider')}</span>
        <div className="flex-1 border-t border-gray-200 dark:border-brand-charcoal" />
      </div>
    </div>
  );
};

/**
 * Outer wrapper — creates PaymentIntent, then renders <Elements> + CheckoutForm.
 */
const ExpressCheckout: React.FC = () => {
  const { items } = useCart();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (items.length === 0) return;

    let cancelled = false;

    const createIntent = async () => {
      try {
        const response = await fetch('/api/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'create-intent',
            items: items.map(item => ({
              id: item.id,
              name: item.madeToOrder ? `${item.name} (Made to Order - 1-2 weeks)` : item.name,
              price: item.price,
              quantity: item.quantity,
              oneOfKind: item.oneOfKind,
              madeToOrder: item.madeToOrder,
              variantId: item.variantId || null,
              slug: item.slug,
              image: item.image,
            })),
            utm: getUtmForCheckout(),
          }),
        });

        if (!response.ok) throw new Error('Failed to create payment intent');

        const data = await response.json();
        if (!cancelled && data.clientSecret) {
          setClientSecret(data.clientSecret);
          // Extract PaymentIntent ID from client secret (pi_xxx_secret_xxx)
          setPaymentIntentId(data.clientSecret.split('_secret_')[0]);
        }
      } catch (err) {
        console.error('Express checkout init failed:', err);
        if (!cancelled) setError(true);
      }
    };

    createIntent();
    return () => { cancelled = true; };
  }, [items]);

  // Silently fail — user still has redirect checkout
  if (error || !clientSecret || !paymentIntentId) return null;

  return (
    <Elements
      stripe={getStripePromise()}
      options={{
        clientSecret,
        appearance: {
          theme: 'flat',
          variables: {
            borderRadius: '8px',
          },
        },
      }}
    >
      <CheckoutForm paymentIntentId={paymentIntentId} />
    </Elements>
  );
};

export default ExpressCheckout;
